import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, Phone, Navigation, Star, Globe, Instagram, Facebook, Youtube, MapPin, QrCode, Tag, BadgeCheck, ChevronRight } from 'lucide-react';
import { SEO } from '../../components/common/SEO';
import { API, waLink, imgSrc, smartCatName as catName } from '../../components/smartqr/smartqrApi';

const ProductImage = ({ product }) => {
  const [failed, setFailed] = useState(false);
  if (!product.image || failed) return null;
  return (
    <div className="relative h-36 sm:h-44 bg-slate-100 overflow-hidden">
      <img src={imgSrc(product.image)} alt={product.name} onError={() => setFailed(true)} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid={`product-image-${product.product_id}`} />
    </div>
  );
};

const ProductCard = ({ p, b }) => (
  <div className="group bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col" data-testid={`product-${p.product_id}`}>
    <ProductImage product={p} />
    <div className="p-3.5 flex flex-col flex-1">
      <div className="flex items-start justify-between gap-2">
        <p className="font-bold text-slate-800 text-sm leading-snug">{p.name}</p>
        {p.price && <span className="shrink-0 bg-orange-50 text-orange-600 border border-orange-100 text-xs font-extrabold px-2 py-0.5 rounded-lg" data-testid={`product-price-${p.product_id}`}>{p.price}</span>}
      </div>
      {p.description && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2 leading-relaxed">{p.description}</p>}
      {b.whatsapp && (
        <a href={waLink(b.whatsapp, `Hi, I am interested in ${p.name} listed on your JustOffs business page. Please share more details.`)} target="_blank" rel="noreferrer"
          className="mt-auto pt-3" data-testid={`enquire-${p.product_id}`}>
          <span className="w-full bg-[#25D366] hover:bg-[#1ebe5b] active:scale-[0.98] text-white py-2.5 px-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow-sm">
            <MessageCircle className="w-3.5 h-3.5" /> Order / Enquiry
          </span>
        </a>
      )}
    </div>
  </div>
);

const linkMeta = {
  google_review_link: { Icon: Star, label: 'Google Review', cls: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100', iconCls: 'text-amber-500 fill-amber-500' },
  website: { Icon: Globe, label: 'Website', cls: 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100', iconCls: 'text-slate-600' },
  instagram: { Icon: Instagram, label: 'Instagram', cls: 'bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100', iconCls: 'text-pink-500' },
  facebook: { Icon: Facebook, label: 'Facebook', cls: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100', iconCls: 'text-blue-600' },
  youtube: { Icon: Youtube, label: 'YouTube', cls: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100', iconCls: 'text-red-500' },
};

export const PublicBusinessPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/smart-qr/b/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 text-center" data-testid="business-not-found">
      <div><p className="text-lg font-bold text-slate-800 mb-2">Business page not found</p><Link to="/" className="text-orange-600 text-sm font-semibold">Go to JustOffs</Link></div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-slate-50 p-10 text-center text-slate-500">Loading...</div>;

  const { business: b, products, offers } = data;
  const isMapLink = /^https?:\/\/(www\.)?(google\.[a-z.]+\/maps|maps\.google|goo\.gl\/maps|maps\.app\.goo\.gl)/i.test(b.address || '');
  // Build the most specific, de-duplicated destination from whatever location fields exist
  const locParts = [];
  [b.address && !isMapLink ? b.address : null, b.area, b.city, b.district, b.state, b.pincode, b.country].forEach((v) => {
    const t = (v || '').trim();
    if (t && !locParts.some((x) => x.toLowerCase() === t.toLowerCase() || x.toLowerCase().includes(t.toLowerCase()))) locParts.push(t);
  });
  const hasLocation = isMapLink || Boolean(b.address || b.city || b.area || b.pincode);
  const directions = isMapLink ? b.address : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([b.business_name, ...locParts].join(', '))}`;
  const links = Object.keys(linkMeta).filter((k) => b[k]);

  return (
    <div className="min-h-screen bg-[#f6f7fb]" data-testid="public-business-page">
      <SEO title={`${b.business_name} - ${b.area}, ${b.city} | JustOffs`} description={`${b.business_name} (${catName(b.category)}) in ${b.area}, ${b.city}. Products, offers, WhatsApp, call and directions.`} />

      {b.is_demo && (
        <div className="bg-amber-400 text-slate-900 text-center text-xs font-bold py-2 px-4" data-testid="demo-banner">
          DEMO / SAMPLE BUSINESS — for demonstration of JustOffs Smart Business QR only. Not a real or verified shop; products, prices and links are examples.
        </div>
      )}
      {/* Header */}
      <div className="relative bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white pt-6 pb-20 px-4 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-orange-500/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="relative max-w-xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[11px] text-slate-300 bg-white/10 border border-white/10 px-3 py-1 rounded-full backdrop-blur-sm" data-testid="justoffs-badge">
            <BadgeCheck className="w-3.5 h-3.5 text-orange-400" /> Listed on JustOffs
          </Link>
          <div className="mt-6 flex items-center gap-4">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white p-2 shadow-xl ring-4 ring-white/10 shrink-0 flex items-center justify-center overflow-hidden">
              <img src={b.logo ? imgSrc(b.logo) : '/logo.png'} alt={b.business_name} className={b.logo ? 'w-full h-full object-cover rounded-xl' : 'w-full object-contain'} data-testid="biz-logo" data-fallback={!b.logo} />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight tracking-tight" data-testid="biz-name">{b.business_name}</h1>
              <span className="inline-block mt-1.5 text-[11px] font-bold tracking-wide uppercase bg-orange-500 text-white px-2.5 py-1 rounded-md">{catName(b.category)}</span>
              <p className="text-slate-300 text-sm mt-2 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-orange-400 shrink-0" /> <span className="truncate">{[b.area, b.city, b.district && b.district !== b.city ? b.district : null].filter(Boolean).join(', ')}</span></p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-xl mx-auto px-4 -mt-12 space-y-6 pb-12">
        {/* Primary actions */}
        <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 p-3 space-y-2" data-testid="action-buttons">
          <div className="grid grid-cols-2 gap-2">
            {b.whatsapp && <a href={waLink(b.whatsapp, `Hi ${b.business_name}, I found you on JustOffs.`)} target="_blank" rel="noreferrer" className="bg-[#25D366] hover:bg-[#1ebe5b] active:scale-[0.98] text-white py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-sm" data-testid="whatsapp-btn"><MessageCircle className="w-5 h-5" /> WHATSAPP</a>}
            {b.phone && <a href={`tel:+${b.phone}`} className="bg-slate-800 hover:bg-slate-900 active:scale-[0.98] text-white py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-sm" data-testid="call-btn"><Phone className="w-5 h-5" /> CALL</a>}
          </div>
          <div className={`grid gap-2 ${b.google_review_link && hasLocation ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {hasLocation && <a href={directions} target="_blank" rel="noreferrer" className="bg-sky-50 hover:bg-sky-100 active:scale-[0.98] text-sky-800 border border-sky-200 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all" data-testid="directions-btn"><Navigation className="w-4 h-4 text-sky-600" /> DIRECTIONS</a>}
            {b.google_review_link && <a href={b.google_review_link} target="_blank" rel="noreferrer" className="bg-amber-50 hover:bg-amber-100 active:scale-[0.98] text-amber-800 border border-amber-200 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all" data-testid="review-btn"><Star className="w-4 h-4 text-amber-500 fill-amber-500" /> GOOGLE REVIEW</a>}
          </div>
        </div>

        {/* Products */}
        {products.length > 0 && (
          <section data-testid="products-section">
            <div className="flex items-end justify-between mb-3 px-1">
              <h2 className="text-base font-extrabold text-slate-800 tracking-tight">Products & Services</h2>
              <span className="text-xs text-slate-400 font-medium">{products.length} item{products.length > 1 ? 's' : ''}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {products.map((p) => <ProductCard key={p.product_id} p={p} b={b} />)}
            </div>
          </section>
        )}

        {/* Offers */}
        {offers.length > 0 && (
          <section data-testid="offers-section">
            <h2 className="text-base font-extrabold text-slate-800 tracking-tight mb-3 px-1">Current JustOffs Offers</h2>
            <div className="space-y-2">
              {offers.map((o) => (
                <Link key={o.id} to={`/deal/${o.id}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 flex gap-3 items-center hover:shadow-md transition-shadow" data-testid={`offer-${o.id}`}>
                  <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center shrink-0"><Tag className="w-5 h-5 text-white" /></div>
                  <div className="min-w-0 flex-1"><p className="font-bold text-slate-800 text-sm truncate">{o.title}</p><p className="text-xs text-slate-500 truncate">{o.offer_type}{o.price ? ` • ₹${o.price}` : ''}</p></div>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* About + Links */}
        <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="about-section">
          <h2 className="text-base font-extrabold text-slate-800 tracking-tight mb-2">About</h2>
          {b.description && <p className="text-sm text-slate-700 leading-relaxed mb-2" data-testid="biz-description">{b.description}</p>}
          <p className="text-sm text-slate-600 leading-relaxed"><span className="font-semibold text-slate-800">{b.business_name}</span> · {catName(b.category)} in {b.area}, {b.city}.</p>
          <p className="text-xs text-slate-500 mt-1" data-testid="biz-location-line">{[b.district, b.state, b.country].filter(Boolean).join(', ')}{b.pincode ? ` – ${b.pincode}` : ''}</p>
          {b.address && !isMapLink && <p className="text-sm text-slate-600 mt-2 flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-orange-500 shrink-0" /> {b.address}</p>}
          {links.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100" data-testid="links-section">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Reviews & Links</p>
              <div className="grid grid-cols-2 gap-2">
                {links.map((k) => { const { Icon, label, cls, iconCls } = linkMeta[k]; return (
                  <a key={k} href={b[k]} target="_blank" rel="noreferrer" className={`flex items-center gap-2 border px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${cls}`} data-testid={`link-${k}`}><Icon className={`w-4 h-4 ${iconCls}`} /> {label}</a>
                ); })}
              </div>
            </div>
          )}
        </section>

        <div className="text-center pt-2">
          <Link to="/smart-business-qr" className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-orange-600 bg-white border border-slate-200 px-4 py-2 rounded-full shadow-sm transition-colors" data-testid="powered-by-link"><QrCode className="w-3.5 h-3.5 text-orange-500" /> Powered by JustOffs Smart Business QR · Get yours free</Link>
        </div>
      </div>
    </div>
  );
};
